import React, { useState } from 'react';
import { APIS } from '../../../../utils/api-factory';
import "./css/validateGst.scss"

function ValidateGSTPage() {
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [stateName, setStateName] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [gstValidationResult, setGstValidationResult] = useState(null);
  const [panValidationResult, setPanValidationResult] = useState(null);
  const [loading, setLoading] = useState({ gst: false, pan: false });

  const validateGST = async () => {
    if (!gstNumber.trim()) {
      setGstValidationResult({ success: false, message: 'Please enter a GST Number' });
      return;
    }
    
    // Clear PAN results when validating GST
    setPanValidationResult(null);
    setPanNumber('');
    setStateName('');
    setStateCode('');
    
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstRegex.test(gstNumber)) {
      setGstValidationResult({ success: false, message: 'Invalid GST Number format' });
      return;
    }

    setLoading({ ...loading, gst: true });
    
    try {
      const response = await fetch(APIS.VALIDATE_GST(gstNumber));
      const json = await response.json();
      
      if (json?.data?.httpResponseCode === 200 || (json?.data?.result !== null && json?.data?.result !== undefined)) {
        const result = json.data.result;
        setGstValidationResult({ 
          success: true, 
          message: 'Valid GST Number - Details fetched successfully',
          details: {
            gstNumber: json.data.gstNumber,
            legalName: result.taxpayerDetails?.lgnm || 'N/A',
            tradeName: result.taxpayerDetails?.tradeNam || 'N/A',
            registrationDate: result.taxpayerDetails?.rgdt || 'N/A',
            status: result.taxpayerDetails?.sts || 'N/A',
            taxpayerType: result.taxpayerDetails?.dty || 'N/A',
            constitutionOfBusiness: result.taxpayerDetails?.ctb || 'N/A',
            address: result.taxpayerDetails?.pradr?.adr || 'N/A',
            natureOfBusiness: result.taxpayerDetails?.nba || [],
            stateJurisdiction: result.taxpayerDetails?.stj || 'N/A',
            filingStatus: result.taxpayerReturnDetails?.filingStatus?.slice(0, 5) || []
          }
        });
      } else {
        setGstValidationResult({ 
          success: false, 
          message: 'GST Number not found or invalid' 
        });
      }
    } catch (error) {
      setGstValidationResult({ 
        success: false, 
        message: 'Error validating GST. Please try again.' 
      });
    } finally {
      setLoading({ ...loading, gst: false });
    }
  };

  const getGSTDetailsByPAN = async () => {
    if (!panNumber.trim()) {
      setPanValidationResult({ success: false, message: 'Please enter a PAN Number' });
      return;
    }
    
    // Clear GST results when validating PAN
    setGstValidationResult(null);
    setGstNumber('');
    
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(panNumber)) {
      setPanValidationResult({ success: false, message: 'Invalid PAN Number format' });
      return;
    }

    setLoading({ ...loading, pan: true });

    try {
      const params = new URLSearchParams({ panNumber });
      if (stateName.trim()) params.append('stateName', stateName);
      if (stateCode.trim()) params.append('stateCode', stateCode);

      const response = await fetch(`https://bre.infitouch.in/gst/getGstDetails?${params.toString()}`);
      const json = await response.json();
      
      if (json?.status === 200 && json?.data?.result?.gstinResList) {
        const result = json.data.result;
        setPanValidationResult({ 
          success: true, 
          message: `Found ${result.count} GST Number(s) linked to this PAN`,
          details: {
            panNumber: json.data.panNumber,
            count: result.count,
            gstList: result.gstinResList
          }
        });
      } else {
        setPanValidationResult({ 
          success: false, 
          message: 'No GST details found for this PAN' 
        });
      }
    } catch (error) {
      setPanValidationResult({ 
        success: false, 
        message: 'Error fetching GST details. Please try again.' 
      });
    } finally {
      setLoading({ ...loading, pan: false });
    }
  };

  const clearGSTResults = () => {
    setGstNumber('');
    setGstValidationResult(null);
  };

  const clearPANResults = () => {
    setPanNumber('');
    setStateName('');
    setStateCode('');
    setPanValidationResult(null);
  };

  return (
    <div className="app-container">
      <div className="w-100">
        <div className="header">
          <i className="fa fa-file-text-o"></i>
          <h1>GST Validation App</h1>
        </div>

        {/* Basic GST Validation Section */}
        <div className="validation-section">
          <div className="section-header">
            <i className="fa fa-check-circle"></i>
            <h2 style={{margin:0}}>Basic GST Validation</h2>
          </div>
          
          <div className="form-group" style={{alignItems:"center"}}>
            <div className="input-wrapper">
              <i className="fa fa-hashtag input-icon"></i>
              <input
                type="text"
                className="form-input"
                placeholder="Enter GST Number (e.g., 27AABCU9603R1ZX)"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                maxLength="15"
              />
            </div>
            <button 
              className="btn btn-primary"
              style={{margin:0}} 
              onClick={validateGST}
              disabled={loading.gst}
            >
              {loading.gst ? (
                <>
                  <i className="fa fa-spinner fa-spin"></i> Validating...
                </>
              ) : (
                <>
                  <i className="fa fa-check"></i> Validate GST
                </>
              )}
            </button>
            {gstValidationResult && (
              <button className="btn btn-clear" onClick={clearGSTResults}>
                <i className="fa fa-times"></i> Clear
              </button>
            )}
          </div>

          {gstValidationResult && (
            <div className={`result-box ${gstValidationResult.success ? 'success' : 'error'}`}>
              <div className="result-header">
                <i className={`fa ${gstValidationResult.success ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                <span>{gstValidationResult.message}</span>
              </div>
              {gstValidationResult.success && gstValidationResult.details && (
                <div className="result-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <strong>GST Number:</strong>
                      <span>{gstValidationResult.details.gstNumber}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Legal Name:</strong>
                      <span>{gstValidationResult.details.legalName}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Trade Name:</strong>
                      <span>{gstValidationResult.details.tradeName}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Status:</strong>
                      <span className={`status-badge ${gstValidationResult.details.status.toLowerCase()}`}>
                        {gstValidationResult.details.status}
                      </span>
                    </div>
                    <div className="detail-item">
                      <strong>Registration Date:</strong>
                      <span>{gstValidationResult.details.registrationDate}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Taxpayer Type:</strong>
                      <span>{gstValidationResult.details.taxpayerType}</span>
                    </div>
                    <div className="detail-item full-width">
                      <strong>Constitution:</strong>
                      <span>{gstValidationResult.details.constitutionOfBusiness}</span>
                    </div>
                    <div className="detail-item full-width">
                      <strong>Address:</strong>
                      <span>{gstValidationResult.details.address}</span>
                    </div>
                    {gstValidationResult.details.natureOfBusiness.length > 0 && (
                      <div className="detail-item full-width">
                        <strong>Nature of Business:</strong>
                        <span>{gstValidationResult.details.natureOfBusiness.join(', ')}</span>
                      </div>
                    )}
                    <div className="detail-item full-width">
                      <strong>State Jurisdiction:</strong>
                      <span>{gstValidationResult.details.stateJurisdiction}</span>
                    </div>
                  </div>
                  
                  {gstValidationResult.details.filingStatus.length > 0 && (
                    <div className="filing-status">
                      <strong>Recent Filing Status:</strong>
                      <div className="filing-table">
                        <div className="filing-row header-row">
                          <span>Return Type</span>
                          <span>Period</span>
                          <span>Filed Date</span>
                          <span>Status</span>
                        </div>
                        {gstValidationResult.details.filingStatus.map((filing, index) => (
                          <div key={index} className="filing-row">
                            <span>{filing.rtntype}</span>
                            <span>{filing.taxp} {filing.fy}</span>
                            <span>{filing.dof}</span>
                            <span className={`status-badge ${filing.status.toLowerCase()}`}>
                              {filing.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* GST Details by PAN Section */}
        <div className="validation-section">
          <div className="section-header">
            <i className="fa fa-id-card-o"></i>
            <h2 style={{margin:0}}>GST Details by PAN</h2>
          </div>
          
          <div className="form-grid">
            <div className="input-wrapper mx-2">
              <i className="fa fa-id-badge input-icon"></i>
              <input
                type="text"
                className="form-input"
                placeholder="Enter PAN Number (e.g., ABCDE1234F)"
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                maxLength="10"
              />
            </div>
            
            <div className="input-wrapper mx-2">
              <i className="fa fa-map-marker input-icon"></i>
              <input
                type="text"
                className="form-input"
                placeholder="State Name (Optional)"
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
              />
            </div>
            
            <div className="input-wrapper mx-2">
              <i className="fa fa-code input-icon"></i>
              <input
                type="text"
                className="form-input"
                placeholder="State Code (Optional)"
                value={stateCode}
                onChange={(e) => setStateCode(e.target.value)}
                maxLength="2"
              />
            </div>
            
            <button 
              className="btn btn-success" 
              onClick={getGSTDetailsByPAN}
              disabled={loading.pan}
            >
              {loading.pan ? (
                <>
                  <i className="fa fa-spinner fa-spin"></i> Fetching...
                </>
              ) : (
                <>
                  <i className="fa fa-search"></i> Get GST Details
                </>
              )}
            </button>
            
            {panValidationResult && (
              <button className="btn btn-clear" onClick={clearPANResults}>
                <i className="fa fa-times"></i> Clear
              </button>
            )}
          </div>

          {panValidationResult && (
            <div className={`result-box ${panValidationResult.success ? 'success' : 'error'}`}>
              <div className="result-header">
                <i className={`fa ${panValidationResult.success ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                <span>{panValidationResult.message}</span>
              </div>
              {panValidationResult.success && panValidationResult.details && (
                <div className="result-details">
                  <div className="detail-item full-width">
                    <strong>PAN Number:</strong>
                    <span>{panValidationResult.details.panNumber}</span>
                  </div>
                  
                  <div className="gst-list">
                    <strong>Linked GST Numbers ({panValidationResult.details.count}):</strong>
                    <div className="gst-cards">
                      {panValidationResult.details.gstList.map((gst, index) => (
                        <div key={index} className="gst-card">
                          <div className="gst-card-header">
                            <span className="gst-number">{gst.gstin}</span>
                            <span className={`status-badge ${gst.authStatus.toLowerCase()}`}>
                              {gst.authStatus}
                            </span>
                          </div>
                          <div className="gst-card-body">
                            <div className="gst-detail">
                              <i className="fa fa-map-marker"></i>
                              <span>{gst.state}</span>
                            </div>
                            <div className="gst-detail">
                              <i className="fa fa-code"></i>
                              <span>State Code: {gst.stateCd}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    
    </div>
  );
}

export default ValidateGSTPage;